import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Zap, Moon, Check } from 'lucide-react';
import { Button } from '../ui/Button';
export function HowItWorks() {
  return <section className="py-20 bg-[#020617]" data-id="element-101">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-102">
        {/* Product Introduction */}
        <div className="mb-20" data-id="element-103">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-104">
            {/* Text Content */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-105">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-106">
                FLOW REGEN™ — Prostate Flow + Circulation Support
              </h2>
              <p className="text-2xl text-[#e6b500] font-semibold mb-8" data-id="element-107">
                The first botanical formula built specifically to release the
                Pelvic Pressure Trap.
              </p>

              {/* Product Image - Mobile Only (between subheading and description) */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="lg:hidden mb-8" data-id="element-108">
                <div className="relative" data-id="element-109">
                  <img src="https://cdn.magicpatterns.com/uploads/cWKD11f9jn62bxd1MqAWKu/Advertorial_JPGs.jpg" alt="Flow Regen Prostate Support bottles" className="w-full h-auto rounded-2xl shadow-2xl" data-id="element-110" />
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#e6b500]/10 to-transparent rounded-2xl pointer-events-none" data-id="element-111" />
                </div>
              </motion.div>

              <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-112">
                Flow Regen combines 8 clinically-studied botanical extracts —
                the same kinds recommended in European vascular guidelines —
                into a premium liquid formula designed to:
              </p>

              <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700 mb-8" data-id="element-113">
                <ul className="space-y-4" data-id="element-114">
                  {['Support pelvic vein tone while you sleep', 'Improve nighttime blood flow in the pelvic region', 'Reduce swelling and congestion around the bladder', 'Help the bladder finally relax — so you stay asleep', 'Promote deeper, more restorative sleep without interruptions'].map((benefit, idx) => <motion.li key={idx} initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: idx * 0.1
                }} className="flex items-start space-x-3" data-id="element-115">
                      <div className="flex-shrink-0 mt-1" data-id="element-116">
                        <div className="bg-emerald-500/20 rounded-full p-1" data-id="element-117">
                          <Check className="w-5 h-5 text-emerald-400" data-id="element-118" />
                        </div>
                      </div>
                      <span className="text-slate-300 font-medium text-[19px]" data-id="element-119">
                        {benefit}
                      </span>
                    </motion.li>)}
                </ul>
              </div>

              {/* CTA Button */}
              <div className="space-y-4" data-id="element-120">
                <Button size="lg" variant="primary" onClick={() => window.location.href = 'https://arella.store/products/flow-regen'} className="w-full text-xl px-8 py-6 shadow-xl shadow-[#e6b500]/10 hover:shadow-[#e6b500]/20 transition-all" data-id="element-121">
                  TRY FLOW REGEN RISK-FREE TODAY
                </Button>
                <p className="text-center text-[19px] text-slate-400 font-medium" data-id="element-122">
                  60-Day Money-Back Guarantee • Free Shipping on 3+ Bottles
                </p>
              </div>
            </motion.div>

            {/* Product Image - Desktop Only */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="hidden lg:flex justify-center lg:justify-end" data-id="element-123">
              <div className="relative" data-id="element-124">
                <img src="https://cdn.magicpatterns.com/uploads/cWKD11f9jn62bxd1MqAWKu/Advertorial_JPGs.jpg" alt="Flow Regen Prostate Support bottles" className="w-full max-w-lg h-auto rounded-2xl shadow-2xl" data-id="element-125" />
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#e6b500]/10 to-transparent rounded-2xl pointer-events-none" data-id="element-126" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Why Liquid Section */}
        <div className="bg-blue-900 rounded-3xl p-8 md:p-12 text-white mb-20 overflow-hidden relative" data-id="element-127">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-800 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-128" />

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-129">
            <div data-id="element-130">
              <h3 className="text-3xl font-bold mb-4" data-id="element-131">
                Why Liquid? Why Not Pills?
              </h3>
              <p className="text-blue-100 text-xl mb-6" data-id="element-132">
                Most supplements come in capsules or tablets that take 30–60
                minutes to absorb — if they absorb at all.
              </p>
              <p className="text-white font-medium text-xl" data-id="element-133">
                Flow Regen's liquid formula begins working in as little as 15
                minutes. One dropper. 10 seconds. Done.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-134">
              {/* Product Usage Image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-135">
                <img src="https://cdn.magicpatterns.com/uploads/bExn2Bb2Wa4o3E2YsgHDh2/14.jpg" alt="Flow Regen liquid drops being added to water" className="w-full h-auto" data-id="element-136" />
              </div>
            </div>
          </div>
        </div>

        {/* 3 Steps */}
        <div className="text-center mb-16" data-id="element-137">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-138">
            How Flow Regen Works: The 10-Second Nighttime Ritual
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-139">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-slate-700 -z-10" data-id="element-140" />

          {[{
          step: '1',
          title: 'Take One Dropper Before Bed',
          desc: 'Fill the dropper, place under your tongue or mix with water. Absorbs almost immediately.',
          icon: Clock
        }, {
          step: '2',
          title: 'The Botanicals Go To Work',
          desc: 'As you drift off, venotonic compounds support pelvic circulation. Veins stay toned.',
          icon: Zap
        }, {
          step: '3',
          title: 'Wake Up Rested — Not Rushed',
          desc: 'With pressure released, your bladder relaxes. No more 3 AM sprints — just deep sleep.',
          icon: Moon
        }].map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.2
        }} viewport={{
          once: true
        }} className="text-center bg-slate-800/50 p-6 rounded-xl border border-slate-700" data-id="element-141">
              <div className="w-24 h-24 mx-auto bg-slate-900 border-4 border-blue-900/50 rounded-full flex items-center justify-center mb-6 relative" data-id="element-142">
                <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center text-white" data-id="element-143">
                  <item.icon className="w-8 h-8" data-id="element-144" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-[19px] border-2 border-slate-900" data-id="element-145">
                  {item.step}
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-146">
                {item.title}
              </h3>
              <p className="text-slate-300 leading-relaxed text-[19px]" data-id="element-147">
                {item.desc}
              </p>
            </motion.div>)}
        </div>

        <div className="mt-16 text-center bg-slate-800/50 p-6 rounded-xl border border-slate-700" data-id="element-148">
          <p className="text-slate-300 font-medium text-[19px]" data-id="element-149">
            Most men notice a difference within the first 7–14 nights. For best
            results, use for at least 60–90 days.
          </p>
        </div>
      </div>
    </section>;
}